import { FormElementMeta } from '@people-science/forms-core';
import { StateEventConfig, StateNodeType } from '@people-science/state-machine';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef
} from 'react';
import { FetchError } from '../../api/api';
import { useUserContext } from '../../context/UserContext';
import {
  DynamicFormContextState,
  useDynamicFormContext
} from '../../dynamic-forms/DynamicFormContext';
import { FormData } from '../../dynamic-forms/models/FormData';
import { useUpsertStudyParticipantEventForm } from '../../hooks/study-participant/useUpsertStudyParticipantEventForm';
import { FormTemplate } from '../../models/FormTemplate';
import { ParticipantEventLog } from '../../models/ParticipantEventLog';
import { reservedDataSourceKeys } from '../../common/hasCompletedFormTemplateFields';
import {
  FinalSubmitTracker,
  createFinalSubmitTracker,
  startOrJoinFinalSubmit
} from './helpers/finalSubmitLatch';

/** Values to submit; the machine's copy is current at transition time, the rendered one lags a render. */
export function submissionFormValues(
  machineValues: Record<string, unknown> | undefined,
  renderedValues: Record<string, unknown> | undefined
): Record<string, unknown> {
  return machineValues ?? renderedValues ?? {};
}

export type StudyEventFormDataSourceProps = {
  studyParticipantId: number;
  participantEventId: number;
  participantEventLogId?: number;
  intendedStartDate?: Date;
  expireDayCount?: number;
  occurrenceCount: number;
  formTemplate: FormTemplate;
  disabled?: boolean;
  formData?: FormData<any>;
  actorUserId?: number;
  accessToken?: string;
  upsertPath?: string;
  skipUserSessionRefresh?: boolean;
  onCreated?: (
    participantEventLog: ParticipantEventLog,
    formData: FormData<any>
  ) => void;
  /** Fires after each successful upsert; isFinal marks the completing one. */
  onSubmitted?: (isFinal: boolean) => void;
  onError?: (error: FetchError) => void;
  completeEvent?: (statePath: string) => Promise<void>;
};

const StudyEventFormDataSourceProvider: React.FC<
  PropsWithChildren & StudyEventFormDataSourceProps
> = props => {
  const {
    studyParticipantId,
    participantEventId,
    participantEventLogId,
    intendedStartDate,
    occurrenceCount,
    formTemplate,
    disabled,
    formData,
    children,
    actorUserId,
    accessToken,
    upsertPath,
    skipUserSessionRefresh,
    onCreated,
    onSubmitted,
    onError
  } = props;
  const { user, updateUserSession } = useUserContext();
  const formContext = useDynamicFormContext<any, any>();
  const {
    stateMachine,
    entitySchema,
    elementPlugins,
    activeStateNodes,
    formValues,
    changedFormValue,
    setIsSubmitting,
    setError,
    subscribe,
    unsubscribe,
    setTransitioningStateEvent
  } = formContext;
  const activeStateNodesRef = useRef(activeStateNodes);
  activeStateNodesRef.current = activeStateNodes;
  const formContextRef = useRef(formContext);
  formContextRef.current = formContext;
  const submitEventRef = useRef<
    | ((
        context: DynamicFormContextState<any, any>,
        userId: number,
        statePath: string,
        isFinal: boolean
      ) => Promise<void>)
    | null
  >(null);
  const onCreatedRef = useRef(onCreated);
  const onSubmittedRef = useRef(onSubmitted);
  const onErrorRef = useRef(onError);
  const shouldResubmitRef = useRef(false);
  const lastIsFinalRef = useRef(false);
  const lastSubmitPathRef = useRef('');
  /** See createFinalSubmitTracker — a terminal state is announced twice. */
  const finalSubmitRef = useRef<FinalSubmitTracker | undefined>(undefined);
  if (!finalSubmitRef.current) {
    finalSubmitRef.current = createFinalSubmitTracker();
  }
  onCreatedRef.current = onCreated;
  onSubmittedRef.current = onSubmitted;
  onErrorRef.current = onError;
  const previousFormValueRef = useRef(changedFormValue);
  const entityIdRef = useRef(formData?.id);
  const stateMachineRef = useRef(stateMachine);
  const startDateRef = useRef(new Date());
  stateMachineRef.current = stateMachine;

  // get all form elements currently displayed in the DynamicForm component
  const formElementMetasRef = useRef<FormElementMeta<any, any>[]>([]);
  formElementMetasRef.current = activeStateNodes?.reduce(
    (elements, stateNode) => {
      elements.push(...(stateNode.meta?.elements || []));
      return elements;
    },
    [] as FormElementMeta<any, any>[]
  );

  const { upsertedEventData, upsertingEventDataError, upsertEventData } =
    useUpsertStudyParticipantEventForm(
      accessToken || upsertPath ? { accessToken, path: upsertPath } : undefined
    );
  // handle state machine transitioning
  const finishTransition = useCallback(() => {
    setTransitioningStateEvent(event => {
      stateMachine?.transition(event?.name);
      return undefined;
    });
    setIsSubmitting(false);
  }, [setTransitioningStateEvent, setIsSubmitting, stateMachine]);
  // update entityId state after creating entity data
  useEffect(() => {
    if (upsertedEventData?.entityData?.id || formData?.id) {
      entityIdRef.current = upsertedEventData?.entityData?.id || formData?.id;
      // console.log('createdEntityData', createdEntityData);
      // setEntityId(createdEntityData?.id || formData?.id);
    }
  }, [formData, upsertedEventData?.entityData?.id]);
  // After updating the event form data, complete the state machine transition.
  // If creating a new form data entry, then notify parent component that a new
  // event data was created.
  useEffect(() => {
    if (upsertedEventData) {
      shouldResubmitRef.current = false;
      finishTransition();
      onSubmittedRef.current?.(lastIsFinalRef.current);
    }
    if (!formData?.id && upsertedEventData?.entityData?.id) {
      const participantEvent = upsertedEventData.participantEvent;
      const participantEventLog = participantEvent.participantEventLogs[0];
      onCreatedRef.current?.(participantEventLog, upsertedEventData.entityData);
    }
  }, [finishTransition, formData?.id, upsertedEventData]);
  // Error handling
  useEffect(() => {
    if (upsertingEventDataError) {
      shouldResubmitRef.current = true;
      // An upsert failure resolves rather than rejects, so the tracker cannot
      // release itself here. Release by path, so a failure on one submit never
      // clears the latch belonging to another.
      finalSubmitRef.current?.releaseIf(lastSubmitPathRef.current);
      setError(upsertingEventDataError);
      onErrorRef.current?.(upsertingEventDataError);
    }
  }, [setError, upsertingEventDataError]);
  // Watch for form state transitions to initiate saving of the form data.
  // A server side call is only performed if for data changes are detected or
  // if setting the completion state.
  const subscriptionDependencyRef = useRef({
    changedFormValue,
    formValues,
    entitySchema
  });
  subscriptionDependencyRef.current = {
    changedFormValue,
    formValues,
    entitySchema
  };
  useEffect(() => {
    if (disabled) {
      return;
    }
    const performSubmit = async (
      context: DynamicFormContextState<any, any>,
      userId: number,
      statePath: string,
      isFinal: boolean
    ) => {
      const { entitySchema, changedFormValue } =
        subscriptionDependencyRef.current;
      const formValues = submissionFormValues(
        context.stateMachine?.currentState?.context?.formValues as
          Record<string, unknown> | undefined,
        subscriptionDependencyRef.current.formValues
      ) as any;
      lastIsFinalRef.current = isFinal;
      lastSubmitPathRef.current = statePath;
      const data = {} as any;
      for (const column of entitySchema?.columns || []) {
        // make sure to only upsert form data of actual entity schema fields
        let value = formValues[column.name];
        if (typeof value !== 'undefined') {
          // run any data transformations or file uploads from plugins before
          // upserting
          const formElementMeta = formElementMetasRef.current.find(
            e => e.dataSource === column.name
          );
          const plugin = elementPlugins.find(
            p => p.type === formElementMeta?.type
          );
          if (plugin?.onBeforeSave) {
            value = await plugin?.onBeforeSave(value, context);
          }
          // assign value for sending for upsert
          data[column.name] = value;
        }
      }
      // check if the column is one of the reserved data source keys
      for (const column of reservedDataSourceKeys) {
        if (typeof formValues[column] !== 'undefined') {
          data[column] = formValues[column];
        }
      }
      data.userId = formData?.userId ?? userId;
      data.studyParticipantId = studyParticipantId;
      if (entityIdRef.current) {
        data.id = entityIdRef.current;
      }
      const duration = Math.round(
        (new Date().getTime() - startDateRef.current.getTime()) / 1000
      );
      if (!intendedStartDate) {
        throw new Error(
          'Participant event form is missing intended start date'
        );
      }
      setIsSubmitting(true);
      await upsertEventData({
        studyParticipantId,
        participantEventId,
        participantEventLogId,
        intendedStartDate,
        occurrenceCount,
        isFinal,
        progress: 0,
        formTemplateId: formTemplate.id,
        version: formTemplate.version,
        statePath,
        data,
        duration
      });
      if (!skipUserSessionRefresh) {
        await updateUserSession();
      }
    };
    const submitEventData = async (
      context: DynamicFormContextState<any, any>,
      userId: number,
      statePath: string,
      isFinal: boolean
    ) => {
      return startOrJoinFinalSubmit(
        finalSubmitRef.current!,
        statePath,
        isFinal,
        shouldResubmitRef,
        () => performSubmit(context, userId, statePath, isFinal)
      );
    };
    submitEventRef.current = submitEventData;
    const handleTransitionEvent = async (
      context: DynamicFormContextState<any, any>,
      event: StateEventConfig
    ) => {
      const { changedFormValue } = subscriptionDependencyRef.current;
      const stateMachine = stateMachineRef.current;
      const stateNode = stateMachine?.currentState?.path
        ? stateMachine?.getStateNode(stateMachine?.currentState?.path)
        : undefined;
      const isFinal = stateNode?.type === StateNodeType.Final;
      const statePath = stateMachine?.currentState?.path || '';
      const submitterId = actorUserId ?? user?.id;
      if (
        submitterId &&
        ((changedFormValue &&
          previousFormValueRef.current !== changedFormValue) ||
          isFinal ||
          shouldResubmitRef.current === true)
      ) {
        previousFormValueRef.current = changedFormValue;
        await submitEventData(context, submitterId, statePath, isFinal);
      } else {
        finishTransition();
      }
    };
    const handleFinalEvent = async (
      context: DynamicFormContextState<any, any>,
      event: StateEventConfig & { statePath?: string }
    ) => {
      const stateMachine = stateMachineRef.current;
      const statePath =
        event?.statePath ||
        stateMachine?.currentState?.path ||
        activeStateNodesRef.current?.[0]?.path ||
        '';
      const submitterId = actorUserId ?? user?.id;
      if (!submitterId || !statePath) {
        finishTransition();
        return;
      }
      if (event?.statePath) {
        await submitEventData(context, submitterId, statePath, true);
        return;
      }
      const stateNode = stateMachine?.getStateNode(statePath);
      const isFinal = stateNode?.type === StateNodeType.Final;

      const hasRequiredFields = stateNode?.meta?.elements?.some(
        (e: FormElementMeta<any, any>) => e.required
      );
      if (isFinal && !hasRequiredFields) {
        await submitEventData(context, submitterId, statePath, isFinal);
      } else {
        finishTransition();
      }
    };
    subscribe('transition', handleTransitionEvent);
    subscribe('final', handleFinalEvent);
    return () => {
      unsubscribe('transition', handleTransitionEvent);
      unsubscribe('final', handleFinalEvent);
    };
  }, [
    disabled,
    elementPlugins,
    finishTransition,
    formData?.userId,
    formTemplate.id,
    formTemplate.version,
    intendedStartDate,
    occurrenceCount,
    participantEventId,
    participantEventLogId,
    studyParticipantId,
    subscribe,
    unsubscribe,
    actorUserId,
    skipUserSessionRefresh,
    updateUserSession,
    accessToken,
    upsertPath,
    upsertEventData,
    user?.id,
    setIsSubmitting
  ]);

  const completeEvent = useCallback(
    async (statePath: string) => {
      const submitterId = actorUserId ?? user?.id;
      if (!submitterId || !submitEventRef.current) {
        throw new Error('Participant event form is not ready to complete');
      }
      await submitEventRef.current(
        formContextRef.current,
        submitterId,
        statePath,
        true
      );
    },
    [actorUserId, user?.id]
  );

  return (
    <StudyEventFormDataSourceContext.Provider
      value={{ ...props, completeEvent }}
    >
      {children}
    </StudyEventFormDataSourceContext.Provider>
  );
};

export const StudyEventFormDataSourceContext =
  createContext<StudyEventFormDataSourceProps>(null!);

export const useStudyEventFormDataSourceContext = () => {
  const context = useContext(StudyEventFormDataSourceContext);
  return context;
};

export default StudyEventFormDataSourceProvider;
